// messages-one-shape — EVERY MESSAGE PATRICIA SENDS HAS ONE SHAPE (Freddi,
// 2026-10-14: "a generic message that will work for both recurring and new
// customers … and the same for moneys and vaccine sections").
//
//   Hi {first}, {the fact}.
//
//   Here's the link to sign up to our app, where you can manage the booking:   ← only with no account
//   {link}
//
//   {the ask}                                                                  ← absent on two states
//
//   Many tks, Patricia
//
// The ask is word for word the same for a customer of twenty years and one who
// walked in this morning: the link line is what tells a new customer how to
// reach "the app", not the ask. And one rule for who the message is for.

import { joinPath } from "./portal-access";
import { toE164Irish } from "./phone";
import type { AccessPerson } from "./types";

export const SIGN_OFF = "Many tks, Patricia";

/** The line before the link, for a person with no account. */
export const LINK_LEAD = "Here’s the link to sign up to our app, where you can manage the booking:";
/** The same line with no stay to come (audit, 2026-10-15): there is no booking to manage, only stays to book. */
export const LINK_LEAD_STAYS = "Here’s the link to sign up to our app, where you can book and manage your stays:";

/**
 * In the slot, where the link will be: it is issued when Patricia taps
 * WhatsApp, not when the slot draws — see `recipientLink`.
 */
export const LINK_PENDING = "(their sign-up link goes in here when you tap WhatsApp)";

/** Every money ask shares these two sentences (Freddi, 2026-10-14; Revolut ruled out the same day). */
export const PAY_ASK =
  "Paying hasn’t changed — a bank transfer, same details as always. Once it’s sent, upload a screenshot of the transfer in the app and I’ll confirm the booking once I’ve checked it.";

const sentence = (fact: string) => (/[.!?]$/.test(fact.trim()) ? fact.trim() : `${fact.trim()}.`);

/** The four paragraphs — three without a link, two-plus-sign-off without an ask. */
export function shapeMessage(m: { first: string; fact: string; ask?: string | null; link?: string | null; linkLead?: string }): string {
  const parts = [`Hi ${m.first}, ${sentence(m.fact)}`];
  if (m.link) parts.push(`${m.linkLead ?? LINK_LEAD}\n${m.link}`);
  if (m.ask) parts.push(m.ask);
  parts.push(SIGN_OFF);
  return parts.join("\n\n");
}

/**
 * A stored line with the link put in after its fact (AC6). The server stores a
 * refusal WITHOUT the link line — a token lives 24 hours, and a line sent days
 * later would carry a dead one — so the slot adds it when it draws. A line
 * stored before this shape (one paragraph) is left exactly as it was written.
 */
export function withLinkLine(stored: string, link: string | null, lead: string = LINK_LEAD): string {
  if (!link) return stored;
  const at = stored.indexOf("\n\n");
  if (at >= 0) return `${stored.slice(0, at)}\n\n${lead}\n${link}${stored.slice(at)}`;
  // A line stored before the one shape is one paragraph (PR #267 review): the link goes in after its
  // first sentence, so a customer with no account is never told to use an app they cannot open.
  const m = /^([\s\S]*?[.!?])\s+([\s\S]+)$/.exec(stored);
  return m ? `${m[1]}\n\n${lead}\n${link}\n\n${m[2]}` : stored;
}

/** The screenshot that would not match a transfer — stored at refusal, three paragraphs. */
export function moneyNotRightLine(first: string, kind: "deposit" | "remainder" | string): string {
  const what = kind === "deposit" ? "deposit" : "balance";
  return shapeMessage({ first, fact: `sorry — I couldn’t match your ${what} screenshot to a transfer`, ask: "Could you check it and send it again in the app?" });
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** `2026-11-03` → `Mon 3 Nov`, or `3 Nov` without the weekday. */
export function stayDay(iso: string, weekday = true): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  const day = `${d} ${MONTHS[m - 1]}`;
  return weekday ? `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${day}` : day;
}

/** What the rule needs of a person — the desk's App access read carries all of it. */
export type MessagePerson = Pick<AccessPerson, "contactId" | "name" | "phone" | "state" | "token" | "usedByEmail" | "lastSignInAt"> & { signInEmails?: string[] };

/** A number WhatsApp can open — the same test `whatsappTo` applies (PR #267 review). */
export const hasDialablePhone = (p: { phone: string | null }) => toE164Irish(p.phone) !== null;
const hasPhone = hasDialablePhone;
/** Every address this person's links were bound with — a reset leaves the newest invite unused, not them unknown. */
const boundTo = (p: MessagePerson, email: string) =>
  (p.usedByEmail ?? "").toLowerCase() === email || (p.signInEmails ?? []).some((e) => e.toLowerCase() === email);

/**
 * WHO THE MESSAGE IS FOR (Freddi, 2026-10-13, ruling the order put to him):
 * whoever sent the thing it is about; with nothing sent, whoever signed in most
 * recently; with nobody signed in, the first registered person in household
 * order; with nobody registered, the first contact. One person — the WhatsApp
 * button goes to one number — and a contact with no phone is passed over at
 * each step for one who has (AC7b). `people` is in household order.
 */
export function pickRecipient(people: readonly MessagePerson[], senderEmail?: string | null): MessagePerson | null {
  if (!people.length) return null;
  const sender = senderEmail?.trim().toLowerCase();
  const steps: ((p: MessagePerson) => boolean)[] = [
    (p) => !!sender && boundTo(p, sender),
    () => false, // the most recent sign-in is a sort, below
    (p) => p.state === "live",
    () => true,
  ];
  for (const pool of [people.filter(hasPhone), people]) {
    if (!pool.length) continue;
    const sent = pool.find(steps[0]!);
    if (sent) return sent;
    const signedIn = pool.filter((p) => p.lastSignInAt).sort((a, b) => Date.parse(b.lastSignInAt!) - Date.parse(a.lastSignInAt!));
    if (signedIn.length) return signedIn[0]!;
    return pool.find(steps[2]!) ?? pool[0]!;
  }
  return people[0]!;
}

/**
 * The link line, by where the person stands with the app (AC2–AC4).
 *
 * `issue: true` means there is no usable link and one should be made — and it
 * is made WHEN PATRICIA TAPS WHATSAPP, not when the slot draws. The slot draws
 * on every household she opens; issuing there would make a link, and mark the
 * person *Link sent*, for every unregistered household she merely looked at.
 * A revoked person gets no link at all: Patricia ended that access on purpose,
 * and App access is where she gives it back.
 */
export function recipientLink(p: MessagePerson | null, origin: string): { url: string | null; issue: boolean } {
  if (!p) return { url: null, issue: false };
  if ((p.state === "invited" || p.state === "part") && p.token) return { url: `${origin}${joinPath(p.token)}`, issue: false };
  if (p.state === "none" || p.state === "expired") return { url: null, issue: true };
  return { url: null, issue: false };
}

/**
 * APP ACCESS'S MESSAGE WHEN NOTHING IS WAITING ON THE CUSTOMER (Freddi,
 * 2026-10-14): the stay named when there is one, then the link — the sign-up
 * link for a person with no account, the app itself for a registered one. When
 * the booking IS waiting on them (a deposit, a card), App access sends that
 * card's message instead; see the desk's `accessMessage`.
 */
export function signUpMessage(m: {
  first: string;
  link: string;
  registered: boolean;
  stay: { dogs: string; are: "is" | "are"; dates: string } | null;
}): string {
  if (m.stay) {
    const fact = `${m.stay.dogs} ${m.stay.are} booked in for ${m.stay.dates}`;
    return m.registered
      ? shapeMessage({ first: m.first, fact, ask: `You can manage the booking in the app:\n${m.link}` })
      : shapeMessage({ first: m.first, fact, link: m.link });
  }
  const lead = m.registered
    ? "here’s the app, where you can book and manage your stays:"
    : "here’s the link to sign up to our app, where you can book and manage your stays:";
  return `Hi ${m.first}, ${lead}\n${m.link}\n\n${SIGN_OFF}`;
}

/**
 * App access's message with no booking to speak of — the fallback where the
 * desk has no household's next step to hand (messages-one-shape, 2026-10-14;
 * was Patricia's "Hey …, so you can submit your bookings" draft of 2026-09-12).
 * The 24 hours stay out of it: the expiry is on the desk's own row.
 */
export function inviteDraftMessage(args: { name: string; url: string; registered?: boolean }): string {
  const first = args.name.trim().split(/\s+/)[0] ?? args.name;
  return signUpMessage({ first, link: args.url, registered: args.registered === true, stay: null });
}
